import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  BriefcaseBusiness,
  CalendarClock,
  DollarSign,
  TrendingUp,
  Plus,
  CalendarPlus,
  FilePlus2,
  ChevronRight,
  HardHat,
  MessageSquareText,
  FileText,
  PenSquare,
  Eye,
  Clock,
  ShieldCheck,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import { projectsApi, calendarApi, legacyLaborPayApi, subFinancialsApi, reportsApi, projectFinancialsApi } from '../api';
import { centsToDollarString } from '../constants';
import { ACTIVE_STATUSES, STATUS_STYLES, statusLabel, fmtDate, isOverdue } from '../constants';
import { APPOINTMENT_TYPES, ITEM_TYPE_LABEL, ITEM_COLORS, fmtTime } from '../calendarConstants';
import { BUILDBROZ_LOGO_URL } from '../../branding';

const OPS_HERO_BG =
  'https://static.prod-images.emergentagent.com/jobs/e28240eb-89e7-4e54-9741-7cd92399b327/images/62e4d3cb82abe09a94bf159352b04da5d83b1dbffccdfb537ad6c46bcd94291c.png';

const todayStr = () => new Date().toISOString().slice(0, 10);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const StatCard = ({ s, onClick }) => (
  <Card onClick={onClick}
    className="relative bg-gradient-to-b from-[#141414] to-[#0C0C0C] border-gray-800 rounded-2xl p-5 cursor-pointer hover:border-[#D4AF37]/50 transition-colors shadow-[0_0_25px_rgba(212,175,55,0.05)]"
    data-testid={`ops-stat-${slug(s.label)}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <span className="h-12 w-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
          <s.icon size={20} className="text-[#D4AF37]" strokeWidth={1.75} />
        </span>
        <span className="font-brand text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] leading-tight max-w-[110px]">{s.label}</span>
      </div>
      <ChevronRight size={18} className="text-[#D4AF37]/70 mt-1" />
    </div>
    <div className="text-4xl font-brand font-black text-white mt-4 tabular-nums truncate">{s.value}</div>
    <div className="text-sm text-gray-500 mt-1.5 truncate">{s.subtext || '—'}</div>
  </Card>
);

const MiniStat = ({ icon: Icon, label, value, warn, testId }) => (
  <div className="flex flex-col items-center gap-1.5 flex-1 px-1" data-testid={testId}>
    <span className="h-10 w-10 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 flex items-center justify-center">
      <Icon size={17} className="text-[#D4AF37]" strokeWidth={1.75} />
    </span>
    <span className="font-brand text-[9px] font-bold uppercase tracking-[0.14em] text-[#D4AF37] text-center leading-tight">{label}</span>
    <span className={`text-2xl font-brand font-black tabular-nums ${warn ? 'text-amber-400' : 'text-white'}`}>{value}</span>
  </div>
);

export const OpsDashboard = () => {
  const navigate = useNavigate();
  const [activeProjects, setActiveProjects] = useState([]);
  const [todayItems, setTodayItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [calLoaded, setCalLoaded] = useState(false);
  const [owed, setOwed] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [completedFin, setCompletedFin] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setRefreshing(true);
    // Amount Owed = positive employee balances + positive subcontractor balances.
    Promise.all([legacyLaborPayApi.byEmployee(), subFinancialsApi.balances({ group_by: 'subcontractor' })])
      .then(([empRes, subRes]) => {
        const emp = empRes.data.reduce((s, r) => s + Math.max(0, r.known_balance_cents), 0);
        const subs = subRes.data.reduce((s, r) => s + Math.max(0, r.known_balance_cents), 0);
        const hasAdvances = subRes.data.some((r) => r.known_balance_cents < 0);
        setOwed({ emp, subs, total: emp + subs, hasAdvances });
      })
      .catch(() => {});
    projectsApi
      .list({ archived: false, sort: 'target_soonest' })
      .then((res) => setActiveProjects(res.data.filter((p) => ACTIVE_STATUSES.includes(p.status))))
      .catch(() => {})
      .finally(() => setLoaded(true));
    reportsApi.executiveSummary().then((res) => setSummaryData(res.data)).catch(() => {});
    projectFinancialsApi.completedSummary().then((res) => setCompletedFin(res.data)).catch(() => {});
    const t = todayStr();
    calendarApi
      .feed({ start_date: t, end_date: t })
      .then((res) => setTodayItems(res.data))
      .catch(() => {})
      .finally(() => { setCalLoaded(true); setLastUpdated(new Date()); setRefreshing(false); });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const activeCount = activeProjects.length;
  const awaiting = summaryData ? { q: summaryData.quotes, co: summaryData.change_orders } : null;

  const appointmentsToday = todayItems.filter(
    (it) => it.source_type === 'manual_event' && APPOINTMENT_TYPES.includes(it.item_type) && it.status !== 'cancelled'
  ).length;

  const scheduleToday = [...todayItems]
    .sort((a, b) => {
      if (a.is_all_day !== b.is_all_day) return a.is_all_day ? 1 : -1;
      return (a.start_time || '') > (b.start_time || '') ? 1 : -1;
    })
    .slice(0, 5);

  const summary = [
    { label: 'Active Projects', value: loaded ? String(activeCount) : '—', icon: BriefcaseBusiness, to: '/ops/projects',
      subtext: loaded ? `${activeCount === 1 ? 'Live Project' : 'Live Projects'}` : null },
    { label: "Today's Appointments", value: calLoaded ? String(appointmentsToday) : '—', icon: CalendarClock, to: '/ops/calendar',
      subtext: 'Scheduled Today' },
    { label: 'Amount Owed', value: owed ? centsToDollarString(owed.total) : '—', icon: DollarSign, to: '/ops/subcontractor-costs?tab=balances',
      subtext: owed ? `Employees ${centsToDollarString(owed.emp)} · Subs ${centsToDollarString(owed.subs)}${owed.hasAdvances ? ' · Some subs have advances/overpayments' : ''}` : null },
    { label: 'Profit Up to Date', icon: TrendingUp, to: '/ops/financials',
      value: completedFin
        ? (completedFin.project_count > 0 ? centsToDollarString(completedFin.gross_profit_cents || 0) : '$0.00')
        : '—',
      subtext: completedFin
        ? (completedFin.project_count > 0
          ? `From ${completedFin.project_count} completed project${completedFin.project_count === 1 ? '' : 's'}`
          : 'No completed projects yet')
        : null },
  ];

  const notViewed = awaiting ? awaiting.q.sent_quote_not_viewed_count + awaiting.co.sent_change_order_not_viewed_count : 0;
  const expired = awaiting ? awaiting.q.expired_sent_quote_count + awaiting.co.expired_sent_change_order_count : 0;

  return (
    <div data-testid="ops-dashboard" className="-m-4 md:-m-8">
      {/* Hero banner */}
      <div className="relative overflow-hidden mb-6" data-testid="ops-dashboard-hero">
        <div className="absolute inset-0 bg-cover bg-right" style={{ backgroundImage: `url(${OPS_HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="relative z-10 px-5 sm:px-8 py-8 sm:py-10 flex items-center gap-4 sm:gap-5">
          <img src={BUILDBROZ_LOGO_URL} alt="BuildBroz" className="h-16 w-16 sm:h-20 sm:w-20 object-contain mix-blend-screen shrink-0" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-brand font-black tracking-tight text-white">
              Build<span className="text-[#D4AF37]">Broz</span> <span className="text-gray-400 font-bold">Ops</span>
            </h2>
            <p className="text-gray-300 mt-1.5 text-sm sm:text-base max-w-md leading-snug">
              Manage projects, schedules, costs, and company operations in one place.
            </p>
            <div className="h-[2px] w-24 bg-gradient-to-r from-[#D4AF37] to-transparent mt-3 rounded" />
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 w-full" height="14" viewBox="0 0 100 14" preserveAspectRatio="none">
          <polyline points="0,2 78,12 100,6" fill="none" stroke="#D4AF37" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      <div className="px-4 md:px-8 pb-4 md:pb-8">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {summary.map((s) => <StatCard key={s.label} s={s} onClick={() => navigate(s.to)} />)}
        </div>

        {/* Awaiting Customer Response */}
        <Card className="bg-gradient-to-b from-[#141414] to-[#0C0C0C] border-gray-800 rounded-2xl p-5 sm:p-6 mb-6" data-testid="ops-awaiting-response">
          <div className="flex items-center gap-3">
            <span className="h-12 w-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
              <MessageSquareText size={20} className="text-[#D4AF37]" strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="font-brand text-sm font-extrabold uppercase tracking-[0.12em] text-white">Awaiting Customer Response</h3>
              <p className="text-sm text-gray-400 mt-0.5">Items that need a customer response or review.</p>
            </div>
          </div>
          <div className="h-px bg-gray-800 my-5" />
          {awaiting ? (
            <div className="flex divide-x divide-gray-800">
              <MiniStat icon={FileText} label="Quotes" value={awaiting.q.sent_quote_count} testId="awaiting-quotes" />
              <MiniStat icon={PenSquare} label="Change Orders" value={awaiting.co.sent_change_order_count} testId="awaiting-cos" />
              <MiniStat icon={Eye} label="Not Viewed" value={notViewed} testId="awaiting-not-viewed" />
              <MiniStat icon={Clock} label="Expired" value={expired} warn={expired > 0} testId="awaiting-expired" />
            </div>
          ) : <div className="text-sm text-gray-500 text-center py-4">Loading…</div>}
          <div className="h-px bg-gray-800 my-5" />
          <div className="flex flex-wrap gap-2.5">
            <Button onClick={() => navigate('/ops/quotes')} className="bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] text-black hover:from-[#D4AF37] hover:to-[#B8941F] font-semibold rounded-lg" data-testid="awaiting-view-quotes"><FileText size={15} /> View Quotes</Button>
            <Button variant="outline" onClick={() => navigate('/ops/change-orders')} className="border-[#D4AF37]/50 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] font-semibold rounded-lg" data-testid="awaiting-view-cos"><PenSquare size={15} /> View Change Orders</Button>
            <Button variant="outline" onClick={() => navigate('/ops/reports?tab=responses')} className="border-[#D4AF37]/50 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] font-semibold rounded-lg" data-testid="awaiting-view-report"><BarChart3 size={15} /> Full Report</Button>
          </div>
        </Card>

        {/* Schedule + attention */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="bg-gradient-to-b from-[#141414] to-[#0C0C0C] border-gray-800 rounded-2xl p-6" data-testid="ops-today-schedule">
            <h3 className="text-lg font-semibold text-white mb-4">Today's Schedule</h3>
            {scheduleToday.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <CalendarClock size={32} className="mx-auto mb-3 text-gray-700" />
                No appointments, site visits, or scheduled work yet.
              </div>
            ) : (
              <div className="space-y-2">
                {scheduleToday.map((it) => (
                  <button
                    key={it.calendar_item_id}
                    onClick={() => navigate(it.source_route)}
                    className="w-full flex items-center gap-3 bg-[#1A1A1A] hover:bg-[#1F1F1F] rounded-lg px-4 py-3 text-left transition-colors"
                    data-testid={`ops-schedule-${it.calendar_item_id}`}
                  >
                    <span className="h-8 w-1 rounded-full shrink-0" style={{ backgroundColor: ITEM_COLORS[it.item_type] }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-medium truncate">{it.title}</div>
                      <div className="text-xs text-gray-500">
                        {it.is_all_day ? 'All day' : fmtTime(it.start_time)} · {ITEM_TYPE_LABEL[it.item_type]}{it.project ? ` · ${it.project.name}` : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card className="bg-gradient-to-b from-[#141414] to-[#0C0C0C] border-gray-800 rounded-2xl p-6" data-testid="ops-project-attention">
            <h3 className="text-lg font-semibold text-white mb-4">Project Attention</h3>
            {activeCount === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <HardHat size={32} className="mx-auto mb-3 text-gray-700" />
                No active projects yet. Projects will appear here when the Projects module is connected.
              </div>
            ) : (
              <div className="space-y-2">
                {activeProjects.slice(0, 5).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/ops/projects/${p.id}`)}
                    className="w-full flex items-center justify-between gap-3 bg-[#1A1A1A] hover:bg-[#1F1F1F] rounded-lg px-4 py-3 text-left transition-colors"
                    data-testid={`ops-attention-${p.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{p.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            STATUS_STYLES[p.status] || 'bg-gray-700 text-gray-300 border-gray-600'
                          }`}
                        >
                          {statusLabel(p.status)}
                        </span>
                        <span className={`text-xs ${isOverdue(p) ? 'text-red-400' : 'text-gray-500'}`}>
                          {fmtDate(p.target_completion_date) || 'No target date'}
                          {isOverdue(p) && ' · Overdue'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-600 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Quick actions */}
        <Card className="bg-gradient-to-b from-[#141414] to-[#0C0C0C] border-gray-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/ops/projects?new=1')}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="ops-quick-new-project"
            >
              <Plus size={16} /> New Project
            </Button>
            <Button
              onClick={() => navigate('/ops/calendar?new=appointment')}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="ops-quick-new-appointment"
            >
              <CalendarPlus size={16} /> New Appointment
            </Button>
            <Button
              onClick={() => navigate('/ops/expenses?new=expense')}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="ops-quick-add-expense"
            >
              <FilePlus2 size={16} /> Add Expense
            </Button>
          </div>
        </Card>

        {/* System status bar */}
        <Card className="bg-[#0D0D0D] border-gray-800 rounded-2xl px-4 sm:px-5 py-3.5 mb-2 flex items-center justify-between gap-3" data-testid="ops-status-bar">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="h-8 w-8 rounded-full border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
              <ShieldCheck size={15} className="text-[#D4AF37]" />
            </span>
            <span className="text-sm text-white font-medium truncate">All systems operational</span>
            <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <span className="text-xs sm:text-sm text-gray-500" data-testid="ops-last-updated">
              Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}
            </span>
            <button onClick={load} disabled={refreshing} className="text-[#D4AF37] hover:text-[#F0CB5C] transition-colors" data-testid="ops-refresh-btn" aria-label="Refresh">
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};
